import { Check, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/Dialog';
import { useMyProfile } from '@/features/clinic';
import { toast } from '@/shared/lib/toast';
import { markAsHandled } from '@/lib/sentry';
import { useSession } from '@/features/auth';
import type { ClinicAssignment } from '@/features/auth/infrastructure/auth-store';
import { useOrganizationSettings } from '@/features/org';
import { Avatar } from '@/shared/ui/Avatar';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { cn } from '@/shared/lib/utils';

interface ClinicSwitcherDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Professional clinic workspace switcher (list layout).
 */
export function ClinicSwitcherDialog({
  open,
  onOpenChange,
}: ClinicSwitcherDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { activeClinic, selectClinic, isSwitchingClinic, syncClinicsFromProfile } = useSession();
  const orgSettingsQuery = useOrganizationSettings({ enabled: open });
  const [filter, setFilter] = useState('');

  const profileQuery = useMyProfile({ enabled: open });

  const clinics: ClinicAssignment[] =
    profileQuery.data?.clinics.map((clinic) => ({
      clinicId: clinic.id,
      clinicName: clinic.displayName,
      status: clinic.status,
    })) ?? [];

  const activeClinicId = profileQuery.data?.activeClinicId ?? activeClinic?.clinicId ?? null;
  const isSwitching = isSwitchingClinic;
  const showFilter = clinics.length > 5;

  const filteredClinics = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return clinics;
    return clinics.filter((c) => c.clinicName.toLowerCase().includes(q));
  }, [clinics, filter]);

  async function handleClinicSelect(clinicId: string): Promise<void> {
    if (isSwitching || clinicId === activeClinicId) {
      return;
    }

    try {
      await selectClinic(clinicId);
      const selected = clinics.find((clinic) => clinic.clinicId === clinicId);
      toast.success({
        title: 'Clinic switched',
        description: selected ? `Now viewing ${selected.clinicName}` : undefined,
      });
      queryClient.clear();
      onOpenChange(false);
    } catch (error) {
      if (error instanceof Error) markAsHandled(error);
      toast.error({
        title: 'Unable to switch clinic',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  }

  useEffect(() => {
    if (!open || !profileQuery.data) {
      return;
    }

    syncClinicsFromProfile(profileQuery.data);
  }, [open, profileQuery.data, syncClinicsFromProfile]);

  useEffect(() => {
    if (!open) setFilter('');
  }, [open]);

  const orgLabel = orgSettingsQuery.data?.displayName?.trim() || 'your organization';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="space-y-1 border-b hairline px-6 py-5 text-left">
          <DialogTitle>Switch clinic</DialogTitle>
          <DialogDescription>
            Choose an active clinic workspace for {orgLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 pb-2 pt-4">
          {showFilter ? (
            <div className="relative mb-3">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden
              />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter clinics…"
                className="h-10 pl-9"
                aria-label="Filter clinics"
              />
            </div>
          ) : null}

          {profileQuery.isLoading ? (
            <div className="space-y-2 py-2" aria-hidden>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-surface-100 px-3 py-3"
                >
                  <div className="size-10 animate-pulse rounded-xl bg-surface-100" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-3 w-2/5 animate-pulse rounded bg-surface-100" />
                    <div className="h-2.5 w-1/4 animate-pulse rounded bg-surface-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {profileQuery.isError ? (
            <div className="rounded-xl bg-coral-50 px-4 py-8 text-center text-sm text-coral-700">
              Unable to load clinics. Please try again.
            </div>
          ) : null}

          {!profileQuery.isLoading && !profileQuery.isError && clinics.length === 0 ? (
            <div className="px-2 py-10 text-center text-sm text-ink-500">
              No clinics are assigned to your account.
            </div>
          ) : null}

          {!profileQuery.isLoading &&
          !profileQuery.isError &&
          clinics.length > 0 &&
          filteredClinics.length === 0 ? (
            <div className="px-2 py-8 text-center text-sm text-ink-500">
              No clinics match “{filter.trim()}”.
            </div>
          ) : null}

          {filteredClinics.length > 0 ? (
            <div className="nice-scroll max-h-[min(52vh,420px)] space-y-1.5 overflow-y-auto pb-2">
              {filteredClinics.map((clinic) => {
                const isActiveClinic = clinic.clinicId === activeClinicId;
                const isOnline = clinic.status === 'active';

                return (
                  <Button
                    key={clinic.clinicId}
                    variant="ghost"
                    type="button"
                    className={cn(
                      'h-auto w-full items-center justify-start gap-3 rounded-xl border px-3 py-3 text-left shadow-none',
                      isActiveClinic
                        ? 'border-brand-400 bg-brand-50/70 ring-2 ring-brand-500/15'
                        : 'border-transparent hover:border-surface-200 hover:bg-surface-50',
                    )}
                    onClick={() => void handleClinicSelect(clinic.clinicId)}
                    disabled={isSwitching}
                  >
                    <Avatar name={clinic.clinicName} size="md" shape="rounded" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-ink-900">
                        {clinic.clinicName}
                      </span>
                      <span className="mt-1 inline-flex items-center gap-2">
                        <Badge
                          variant={isOnline ? 'mint' : 'neutral'}
                          className="rounded-md px-1.5 py-0 text-[10px] capitalize"
                        >
                          {isOnline ? 'Active' : (clinic.status ?? 'inactive')}
                        </Badge>
                        {isActiveClinic ? (
                          <span className="text-[11px] font-semibold text-brand-700">Current</span>
                        ) : null}
                      </span>
                    </span>
                    {isActiveClinic ? (
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white">
                        <Check className="size-3.5" aria-hidden />
                      </span>
                    ) : (
                      <span className="text-[12px] font-semibold text-brand-600">Switch</span>
                    )}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="border-t hairline bg-surface-50/80 px-6 py-3 text-[11px] text-ink-500">
          Switching refreshes clinic data for the workspace.
        </div>
      </DialogContent>
    </Dialog>
  );
}
